// Radha — application shell.
// Owns the sidebar navigation across the 9 module dashboards and a top bar with
// the signed-in user + logout. Each page component lives in pages/ and is mounted
// by the lightweight hash router below (swap for react-router in your build if
// preferred — the route table is the same).
import React, { useEffect, useState } from "react";
import {
  LayoutGrid, FileText, TrendingUp, Receipt, PhoneCall, Gauge, Scale, Banknote, Send, LogOut,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { Claims } from "../lib/auth";

import Collections from "../pages/Collections.jsx";
import Disputes from "../pages/Disputes.jsx";
import Contracts from "../pages/Contracts.jsx";
import Forecast from "../pages/Forecast.jsx";
import Invoicing from "../pages/Invoicing.jsx";
import Reminders from "../pages/Reminders.jsx";
import Reconciliation from "../pages/Reconciliation.jsx";
import Cashflow from "../pages/Cashflow.jsx";
import Crm from "../pages/Crm.jsx";

export interface Route {
  code: string;
  slug: string;
  name: string;
  icon: LucideIcon;
  Page: React.ComponentType;
  roles?: string[]; // if set, only these roles (or admin) see the nav entry
}

// Single source of truth for navigation + routing.
export const ROUTES: Route[] = [
  { code: "M1", slug: "collections", name: "Collections Copilot", icon: PhoneCall, Page: Collections },
  { code: "M2", slug: "disputes", name: "Dispute Resolution", icon: Scale, Page: Disputes },
  { code: "M3", slug: "contracts", name: "Contract MIS", icon: FileText, Page: Contracts },
  { code: "M4", slug: "forecast", name: "Revenue Forecast", icon: TrendingUp, Page: Forecast },
  { code: "M5", slug: "invoicing", name: "Invoicing Engine", icon: Receipt, Page: Invoicing },
  { code: "M6", slug: "reminders", name: "Payment Reminders", icon: Send, Page: Reminders },
  { code: "M7", slug: "reconciliation", name: "Cash Application", icon: Banknote, Page: Reconciliation },
  { code: "M8", slug: "cashflow", name: "Cash Flow Forecast", icon: Gauge, Page: Cashflow },
  { code: "M9", slug: "crm", name: "CRM Pipeline", icon: LayoutGrid, Page: Crm },
];

// Role-gated navigation. Recovered from the claude.ai edit that introduced
// Route.roles; the DPO Console and Privacy Portal routes it also added are NOT
// wired up here — DsarDpoConsole has no backend (no /dsar endpoints exist) and
// DsarPortal was never recovered at all. Add them back with their service.
function visibleRoutes(role: string | undefined): Route[] {
  return ROUTES.filter((r) => !r.roles || (role && (r.roles.includes(role) || role === "admin")));
}

function currentSlug(): string {
  const h = window.location.hash.replace(/^#\/?/, "");
  return ROUTES.some((r) => r.slug === h) ? h : ROUTES[0].slug;
}

export default function AppShell({ claims, onLogout }: { claims: Claims | null; onLogout: () => void }) {
  const [slug, setSlug] = useState<string>(currentSlug());

  useEffect(() => {
    const onHash = () => setSlug(currentSlug());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const route = ROUTES.find((r) => r.slug === slug) ?? ROUTES[0];
  const Page = route.Page;
  const nav = visibleRoutes(claims?.role);

  // The page components currently render their own sidebar (self-contained for
  // standalone preview). When integrating, strip the in-page sidebar and let the
  // shell own navigation; until then we render the page as-is.
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--surface-2)" }}>
      <nav style={{ width: 224, background: "#0E1118", color: "#E7E9EE", padding: "20px 12px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 18px" }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: "#6E44D4", display: "grid", placeItems: "center", fontFamily: "monospace", fontWeight: 600 }}>र</div>
          <div style={{ fontWeight: 600 }}>Radha</div>
        </div>
        {nav.map((r) => {
          const Icon = r.icon;
          const active = r.slug === slug;
          return (
            <a key={r.code} href={`#/${r.slug}`}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, marginBottom: 2, textDecoration: "none", background: active ? "#1B54F8" : "transparent", color: active ? "#fff" : "#AEB4C0" }}>
              <Icon size={15} /><span style={{ fontSize: 12.5 }}>{r.name}</span>
              <span style={{ marginLeft: "auto", fontFamily: "monospace", fontSize: 8, opacity: 0.6 }}>{r.code}</span>
            </a>
          );
        })}
        <div style={{ marginTop: 24, borderTop: "0.5px solid #2A2F3A", paddingTop: 14 }}>
          <div style={{ fontSize: 11, color: "#AEB4C0", padding: "0 8px" }}>{claims?.role ?? "—"}</div>
          <button onClick={onLogout}
            style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, background: "transparent", border: "none", color: "#7A8190", cursor: "pointer", padding: "6px 8px", fontSize: 12 }}>
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </nav>

      <div style={{ flex: 1, minWidth: 0 }}>
        <Page />
      </div>
    </div>
  );
}
